import { ErrorHandler, Injectable } from '@angular/core';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { addAngularError } from '@datadog/browser-rum-angular';
import { secureRoutesFor } from '@presencelearning/auth';
import { getLogger, type LoggingConfig, type SampleRates } from '@presencelearning/logging';
import { releaseVersion } from '../environments/release';

const SERVICE = 'future-room';
const SITE = 'datadoghq.com';

/** Origins that receive the Sirius bearer token and RUM trace headers. */
export function apiOrigins(apps: Record<string, { url: string }>, doorUrl = ''): string[] {
  return secureRoutesFor(apps, [doorUrl]);
}

export function matchesApiOrigin(url: string, origins: readonly string[]): boolean {
  try {
    return origins.includes(new URL(url).origin);
  } catch {
    return false;
  }
}

/**
 * Per-level keep rates. The logging package forces the SDK session sample to
 * 100 and does not accept `forwardErrorsToLogs` or `forwardConsoleLogs`.
 * Production keeps warn and error. Other environments keep every level.
 */
export function sampleRatesFor(env: string): SampleRates {
  if (env === 'production') {
    return { debug: 0, info: 0, warn: 1, error: 1 };
  }
  return { debug: 1, info: 1, warn: 1, error: 1 };
}

export function loggingConfig(env: string, clientToken: string): LoggingConfig | null {
  if (!clientToken) {
    return null;
  }
  return {
    service: SERVICE,
    env,
    version: releaseVersion,
    clientToken,
    site: SITE,
    sampleRates: sampleRatesFor(env),
  };
}

const FORWARDED_CONSOLE_LEVELS = ['warn', 'error'] as const;

/** Console methods copied into the logging package. The SDK will not forward them. */
export function forwardedConsoleLevels(): readonly ('warn' | 'error')[] {
  return FORWARDED_CONSOLE_LEVELS;
}

const MAX_CONSOLE_TEXT = 2048;

function isPlainObject(arg: unknown): arg is Record<string, unknown> {
  if (arg === null || typeof arg !== 'object' || Array.isArray(arg)) {
    return false;
  }
  const prototype: unknown = Object.getPrototypeOf(arg);
  return prototype === Object.prototype || prototype === null;
}

/** JSON for a plain object, capped so one console arg cannot dominate a log line. */
function plainObjectText(arg: Record<string, unknown>): string {
  let text: string;
  try {
    text = JSON.stringify(arg);
  } catch {
    try {
      text = String(arg);
    } catch {
      text = '[unserializable]';
    }
  }
  return text.length > MAX_CONSOLE_TEXT ? text.slice(0, MAX_CONSOLE_TEXT) : text;
}

function consoleText(args: unknown[]): string {
  return args
    .map(arg => {
      if (typeof arg === 'string') {
        return arg;
      }
      if (arg instanceof Error) {
        return arg.message;
      }
      if (isPlainObject(arg)) {
        return plainObjectText(arg);
      }
      return '';
    })
    .filter(part => part !== '')
    .join(' ');
}

/**
 * Copy console warn and error into `getLogger('console')` after logging is
 * initialized. The package inits the logs SDK with `forwardErrorsToLogs: false`
 * and leaves `forwardConsoleLogs` unset, so those calls would otherwise never
 * be uploaded. `getLogger` scrubs the message first.
 */
export function installConsoleLogForwarding(): void {
  const logger = getLogger('console');
  for (const level of FORWARDED_CONSOLE_LEVELS) {
    // eslint-disable-next-line no-console -- this wrapper is the console-to-logs seam
    const original = console[level].bind(console);
    // eslint-disable-next-line no-console -- replace the method the wrapper calls
    console[level] = (...args: unknown[]) => {
      original(...args);
      const message = consoleText(args) || level;
      if (level === 'error') {
        logger.error(args.find(arg => arg instanceof Error) ?? message, message);
        return;
      }
      logger.warn(message);
    };
  }
}

export function rumInitConfiguration(
  applicationId: string,
  clientToken: string,
  env: string,
  origins: readonly string[]
): RumInitConfiguration | null {
  if (!applicationId || !clientToken) {
    return null;
  }
  return {
    applicationId,
    clientToken,
    site: SITE,
    service: SERVICE,
    env,
    version: releaseVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    traceContextInjection: 'sampled',
    allowedTracingUrls: [
      {
        match: (url: string) => matchesApiOrigin(url, origins),
        propagatorTypes: ['datadog', 'tracecontext'],
      },
    ],
  };
}

/** Reports an unhandled error to RUM and to the logging package. */
@Injectable()
export class FutureRoomErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    addAngularError(error);
    getLogger('angular').error(error, 'unhandled error');
    super.handleError(error);
  }
}
